import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { error: Error | null }

/**
 * The last line of defence against a blank white screen.
 *
 * React unmounts the entire tree on an uncaught render error — with nothing
 * catching it, that is a white page and no way back except closing the app.
 * One boundary at the root turns that into a recoverable screen instead,
 * whatever threw: a third-party script's callback (Google Identity Services'
 * button, notably — its callback runs outside React's own event handling, so
 * an error inside it is exactly the kind of thing this exists to catch), a
 * bad response shape, anything.
 */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled error in the app tree', error, info.componentStack)
  }

  override render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-white px-8 text-center">
        <p className="text-lg font-bold text-slate-900">Une erreur est survenue</p>
        <p className="text-sm text-slate-500">
          Veuillez recharger la page. Si le problème persiste, contactez le support.
        </p>
        <button
          type="button"
          onClick={() => {
            this.setState({ error: null })
            window.location.reload()
          }}
          className="bg-brand-600 rounded-md px-5 py-2.5 text-sm font-medium text-white"
        >
          Recharger
        </button>
      </div>
    )
  }
}
